import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Hash, Bell, BellOff, Users, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";

export function SpaceCard({ space, onToggle }) {
  const [busy, setBusy] = useState(false);
  const subscribe = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/spaces/${space.slug}/subscribe`);
      onToggle(space.slug, r.data);
      window.dispatchEvent(new Event("spaces-changed"));
    } catch (e) {
      toast.error("Could not update subscription");
    } finally { setBusy(false); }
  };
  return (
    <div className="border border-border bg-card rounded-xl p-5 flex flex-col" data-testid={`browse-space-${space.slug}`}>
      <div className="flex items-start justify-between gap-3">
        <span className="grid place-items-center w-10 h-10 rounded-lg bg-secondary text-cobalt shrink-0"><Hash size={18} /></span>
        {space.premium && <span className="inline-flex items-center gap-1 text-[10px] bg-cobalt text-white rounded-full px-2 py-0.5"><Sparkles size={10} /> Pro</span>}
      </div>
      <Link to={`/space/${space.slug}`} className="mt-3 font-semibold hover:text-cobalt transition-colors">{space.name}</Link>
      <p className="mt-1 text-sm text-muted-foreground leading-relaxed line-clamp-2 flex-1">{space.description}</p>
      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-muted-foreground font-mono inline-flex items-center gap-1"><Users size={12} /> {space.subscriber_count ?? 0}</span>
        <button onClick={subscribe} disabled={busy} data-testid={`browse-subscribe-${space.slug}`}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
            space.subscribed ? "border border-border text-foreground hover:bg-secondary/60" : "bg-cobalt text-white hover:bg-gold-hover"}`}>
          {space.subscribed ? <><BellOff size={13} /> Subscribed</> : <><Bell size={13} /> Subscribe</>}
        </button>
      </div>
    </div>
  );
}

export default function SpacesBrowse() {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/spaces").then((r) => setSpaces(r.data)).finally(() => setLoading(false));
  }, []);

  const onToggle = (slug, data) =>
    setSpaces((prev) => prev.map((s) => s.slug === slug ? { ...s, ...data } : s));

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Discover" title="Browse spaces"
        subtitle="Find the walls that matter to you and subscribe to follow the conversation." />
      {loading ? (
        <div className="text-center py-16 text-muted-foreground eyebrow">Loading…</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {spaces.map((s) => <SpaceCard key={s.space_id} space={s} onToggle={onToggle} />)}
        </div>
      )}
    </div>
  );
}
